import React, { useState } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import { NorthIndianSvgChart, SouthIndianSvgChart } from './ChartViewer';
import { Layers, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { getVargaRoleNote } from '../utils/presentationHelper';

interface AdvancedChartRailProps {
  kundali: CompleteKundali;
  relevantVargas: string[];
  lang: Language;
  chartStyle: ChartStyle;
  onStyleChange?: (style: ChartStyle) => void;
  className?: string;
}

const VARGA_NAMES: Record<string, { en: string; hi: string; descEn: string; descHi: string }> = {
  D1: { en: 'Rashi', hi: 'लग्न चक्र', descEn: 'Physical Body & Natal Foundation', descHi: 'शारीरिक संरचना एवं आधारभूत प्रारब्ध' },
  D9: { en: 'Navamsha', hi: 'नवमांश चक्र', descEn: 'Spouse, Dharma & Planetary Dignity', descHi: 'दांपत्य, धर्म व ग्रहों का सूक्ष्म बल' },
  D10: { en: 'Dashamsha', hi: 'दशमांश चक्र', descEn: 'Profession, Status & Executive Karma', descHi: 'आजीविका, पद, प्रतिष्ठा व कर्म' },
  D7: { en: 'Saptamsha', hi: 'सप्तमांश चक्र', descEn: 'Progeny, Children & Creative Lineage', descHi: 'संतान सुख, वंश वृद्धि व रचनात्मकता' },
  D4: { en: 'Chaturthamsha', hi: 'चतुर्थांश चक्र', descEn: 'Fixed Assets, Real Estate & Luck', descHi: 'अचल संपत्ति, भूमि, गृह व भाग्य' },
  D16: { en: 'Shodashamsha', hi: 'षोडशांश चक्र', descEn: 'Vehicles, Conveyance & Material Comforts', descHi: 'वाहन, सुख-साधन व भौतिक आनंद' },
  D24: { en: 'Chaturvimshamsha', hi: 'चतुर्विंशांश चक्र', descEn: 'Higher Education, Learning & Intellect', descHi: 'उच्च विद्या, ज्ञानार्जन व मेधा' },
  D30: { en: 'Trimshamsha', hi: 'त्रिंशांश चक्र', descEn: 'Misfortunes, Health Vulnerabilities & Arishta', descHi: 'अनिष्ट, रोग, बाधाएं व संकट निवारण' },
  D60: { en: 'Shashtiamsha', hi: 'षष्ट्यंश चक्र', descEn: 'Past Karma, Deep Prarabdha & Ultimate Truth', descHi: 'पूर्व जन्म के संचित कर्म व सूक्ष्म फलादेश' },
  D2: { en: 'Hora', hi: 'होरा चक्र', descEn: 'Wealth Accumulation & Financial Reserves', descHi: 'धन संग्रह, संपदा व वित्तीय स्थिति' },
  D3: { en: 'Drekkana', hi: 'द्रेष्काण चक्र', descEn: 'Siblings, Courage & Initiative', descHi: 'भाई-बहन, पराक्रम व उद्यमशीलता' },
  D12: { en: 'Dwadashamsha', hi: 'द्वादशांश चक्र', descEn: 'Parents, Ancestral Lineage & Heritage', descHi: 'माता-पिता, पितृ कुल व पारिवारिक विरासत' }
};

export const AdvancedChartRail: React.FC<AdvancedChartRailProps> = ({
  kundali,
  relevantVargas,
  lang,
  chartStyle,
  onStyleChange,
  className = ''
}) => {
  const isHi = lang === 'hi';
  
  // Thematic varga from analysis
  const primaryThemeVarga = relevantVargas.find((v) => v !== 'D1' && v !== 'D9') || relevantVargas[0] || 'D10';

  const [selectedVarga, setSelectedVarga] = useState<string>(primaryThemeVarga);
  const [mobileChartIndex, setMobileChartIndex] = useState<number>(0);

  // Triple rail vargas: D1, selected thematic varga, and D9 Navamsha
  const coreDisplayVargas = Array.from(new Set(['D1', selectedVarga, 'D9']));

  // All vargas available for quick tab switching
  const allAvailableVargas = Array.from(new Set(['D1', 'D9', primaryThemeVarga, ...relevantVargas]));

  const nextMobileChart = () => {
    setMobileChartIndex((prev) => (prev + 1) % coreDisplayVargas.length);
  };

  const prevMobileChart = () => {
    setMobileChartIndex((prev) => (prev - 1 + coreDisplayVargas.length) % coreDisplayVargas.length);
  };

  return (
    <div
      id="advanced-chart-rail"
      className={`bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-5 shadow-xs space-y-4 ${className}`}
    >
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-astro-border pb-3">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-astro-accent" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-astro-text">
            {isHi ? 'प्रासंगिक विभागीय वर्ग चक्र रेल (D1 + विषय वर्ग + D9)' : 'Synchronized Divisional Chart Rail (D1 + Theme Varga + D9)'}
          </h3>
        </div>

        {/* Global Chart Style Switcher */}
        {onStyleChange && (
          <div className="flex items-center gap-1 bg-astro-card p-0.5 rounded-lg text-xs self-end sm:self-auto border border-astro-border">
            <button
              onClick={() => onStyleChange('north')}
              className={`px-2.5 py-1 rounded font-medium transition-all min-h-[36px] touch-manipulation cursor-pointer ${
                chartStyle === 'north'
                  ? 'bg-astro-primary text-white shadow-2xs font-semibold'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              {isHi ? 'उत्तर भारतीय' : 'North Indian'}
            </button>
            <button
              onClick={() => onStyleChange('south')}
              className={`px-2.5 py-1 rounded font-medium transition-all min-h-[36px] touch-manipulation cursor-pointer ${
                chartStyle === 'south'
                  ? 'bg-astro-primary text-white shadow-2xs font-semibold'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              {isHi ? 'दक्षिण भारतीय' : 'South Indian'}
            </button>
          </div>
        )}
      </div>

      {/* Horizontal Scrolling Varga Selector Tabs for Thematic Selection */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 feature-nav-scroll">
        <span className="text-[11px] font-bold text-astro-secondary-text whitespace-nowrap uppercase tracking-wider mr-1">
          {isHi ? 'विषय वर्ग:' : 'Thematic Varga:'}
        </span>
        {allAvailableVargas.map((vCode) => {
          const vMeta = VARGA_NAMES[vCode] || {
            en: `${vCode} Chart`,
            hi: `${vCode} चक्र`,
            descEn: 'Vedic Harmonic',
            descHi: 'वैदिक वर्ग'
          };
          const isSelected = selectedVarga === vCode;

          return (
            <button
              key={vCode}
              onClick={() => setSelectedVarga(vCode)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs whitespace-nowrap border transition-all min-h-[44px] touch-manipulation cursor-pointer ${
                isSelected
                  ? 'bg-astro-primary text-white border-astro-primary shadow-xs font-bold ring-2 ring-astro-accent/30'
                  : 'bg-astro-card border-astro-border text-astro-text hover:border-astro-accent'
              }`}
            >
              <span className="font-mono font-bold text-astro-accent">{vCode}</span>
              <span>{isHi ? vMeta.hi : vMeta.en}</span>
            </button>
          );
        })}
      </div>

      {/* DESKTOP & TABLET: Multi-Chart Synchronized Rail */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
        {coreDisplayVargas.map((vCode) => {
          const vMeta = VARGA_NAMES[vCode] || {
            en: `${vCode} Harmonic`,
            hi: `${vCode} वर्ग`,
            descEn: 'Vedic Harmonic Chart',
            descHi: 'वैदिक वर्ग चक्र'
          };
          const vChart = kundali.charts[vCode] || kundali.charts.D1;
          const roleNote = getVargaRoleNote(vCode, lang);

          return (
            <div
              key={vCode}
              className="flex flex-col items-center p-3.5 rounded-xl border border-astro-border bg-astro-bg space-y-2.5 transition-all shadow-xs"
            >
              {/* Varga Header & Role Note */}
              <div className="w-full px-1 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-astro-text font-serif">
                    {isHi ? vMeta.hi : vMeta.en}
                  </span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-astro-accent/15 border border-astro-accent/30 text-astro-accent font-bold">
                    {vCode}
                  </span>
                </div>
                {/* Varga Role Microcopy */}
                <p className="text-[11px] text-astro-secondary-text leading-snug">
                  {isHi ? roleNote.roleHi : roleNote.roleEn}
                </p>
              </div>

              {/* Chart SVG Canvas */}
              <div className="w-full max-w-[280px] aspect-square">
                {chartStyle === 'south' ? (
                  <SouthIndianSvgChart
                    chartData={vChart}
                    planets={vChart?.planets || kundali.planets}
                    ashtakvarga={kundali.ashtakvarga}
                    lang={lang}
                    showDegrees={true}
                  />
                ) : (
                  <NorthIndianSvgChart
                    chartData={vChart}
                    planets={vChart?.planets || kundali.planets}
                    ashtakvarga={kundali.ashtakvarga}
                    lang={lang}
                    showDegrees={true}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* MOBILE (<768px): STRICT SINGLE-CHART SWIPE VIEWPORT */}
      <div className="block md:hidden space-y-3 pt-1">
        {(() => {
          const vCode = coreDisplayVargas[mobileChartIndex] || 'D1';
          const vMeta = VARGA_NAMES[vCode] || {
            en: `${vCode} Harmonic`,
            hi: `${vCode} वर्ग`,
            descEn: 'Vedic Harmonic Chart',
            descHi: 'वैदिक वर्ग चक्र'
          };
          const vChart = kundali.charts[vCode] || kundali.charts.D1;
          const roleNote = getVargaRoleNote(vCode, lang);

          return (
            <div className="flex flex-col items-center p-3.5 rounded-xl border border-astro-border bg-astro-bg space-y-3 shadow-xs">
              {/* Single Chart Header with Mobile Navigation */}
              <div className="w-full flex items-center justify-between">
                <button
                  onClick={prevMobileChart}
                  className="p-2 rounded-lg bg-astro-card border border-astro-border text-astro-text min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation cursor-pointer"
                  aria-label="Previous chart"
                >
                  <ChevronLeft className="w-4 h-4 text-astro-primary" />
                </button>

                <div className="text-center px-2">
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="text-xs font-bold text-astro-text font-serif">
                      {isHi ? vMeta.hi : vMeta.en}
                    </span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-astro-accent/15 text-astro-accent font-bold">
                      {vCode}
                    </span>
                  </div>
                  <p className="text-[10px] text-astro-secondary-text mt-0.5">
                    {isHi ? roleNote.roleHi : roleNote.roleEn}
                  </p>
                </div>

                <button
                  onClick={nextMobileChart}
                  className="p-2 rounded-lg bg-astro-card border border-astro-border text-astro-text min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation cursor-pointer"
                  aria-label="Next chart"
                >
                  <ChevronRight className="w-4 h-4 text-astro-primary" />
                </button>
              </div>

              {/* Single SVG Canvas (Max 300px on mobile) */}
              <div className="w-full max-w-[300px] aspect-square mx-auto">
                {chartStyle === 'south' ? (
                  <SouthIndianSvgChart
                    chartData={vChart}
                    planets={vChart?.planets || kundali.planets}
                    ashtakvarga={kundali.ashtakvarga}
                    lang={lang}
                    showDegrees={true}
                  />
                ) : (
                  <NorthIndianSvgChart
                    chartData={vChart}
                    planets={vChart?.planets || kundali.planets}
                    ashtakvarga={kundali.ashtakvarga}
                    lang={lang}
                    showDegrees={true}
                  />
                )}
              </div>

              {/* Pagination Dots (Mobile) */}
              <div className="flex items-center justify-center gap-2 pt-1">
                {coreDisplayVargas.map((code, idx) => (
                  <button
                    key={code}
                    onClick={() => setMobileChartIndex(idx)}
                    className={`h-2 rounded-full transition-all cursor-pointer ${
                      mobileChartIndex === idx
                        ? 'w-6 bg-astro-primary'
                        : 'w-2 bg-astro-border'
                    }`}
                    aria-label={`View ${code} chart`}
                  />
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};
